import React, { useEffect } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  ClipboardList,
  FileWarning,
  ArrowRight,
} from 'lucide-react';

import { useAssessment } from './hook/useAssessment';
import {
  //   AssessmentWrapper,
  //   AssessmentContainer,
  HeaderSection,
  SectionTitle,
  SectionDescription,
  ActionButtons,
  DashboardButton,
  CoverPanel,
  CoverMetaGrid,
  CoverMetaItem,
  CoverMetaLabel,
  CoverMetaValue,
  ScoreRing,
  ScoreNum,
  ScoreLabel,
  Section,
  SectionLabel,
  CountBadge,
  PoList,
  PoItem,
  PoCode,
  PoDescription,
  PoFieldGrid,
  PoFieldLabel,
  PoFieldValue,
  FindingList,
  FindingItem,
  FindingIcon,
  FindingBody,
  FindingHeader,
  FindingCode,
  StatusBadge,
  FindingDescription,
  FindingReason,
  LoadingState,
  EmptyState,
  StatusMessage,
  CheckAgainButton,
  GlobalError,
} from './Assessment.styles';
import { ContinueButton } from '../Processing/processing.styles';

const statusIcon = (status) => {
  if (status === 'COMPLIANT') return <CheckCircle2 />;
  if (status === 'INSUFFICIENT_EVIDENCE') return <AlertTriangle />;
  return <XCircle />;
};

const FindingsSection = ({ title, results }) => {
  if (!results || results.length === 0) return null;

  const compliantCount = results.filter((r) => r.status === 'COMPLIANT').length;

  return (
    <Section>
      <SectionLabel>
        {title}
        <CountBadge>
          {compliantCount} / {results.length} Compliant
        </CountBadge>
      </SectionLabel>
      <FindingList>
        {results.map((r, idx) => (
          <FindingItem key={`${r.poCode}-${idx}`}>
            <FindingIcon $status={r.status}>{statusIcon(r.status)}</FindingIcon>
            <FindingBody>
              <FindingHeader>
                <FindingCode>{r.poCode}</FindingCode>
                <StatusBadge $status={r.status}>{r.status?.replace(/_/g, ' ')}</StatusBadge>
              </FindingHeader>
              {r.originalPoDescription && (
                <FindingDescription>{r.originalPoDescription}</FindingDescription>
              )}
              {r.reason && <FindingReason>{r.reason}</FindingReason>}
            </FindingBody>
          </FindingItem>
        ))}
      </FindingList>
    </Section>
  );
};

const Assessment = ({ listId, onDashboard, onContinue }) => {
  const { assessMutation, assessmentQuery, isComplete, trigger } = useAssessment(listId);

  useEffect(() => {
    if (listId) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  const handleCheckAgain = () => trigger();

  const data = assessmentQuery.data?.data;
  const assessmentData = data?.assessmentData ?? [];
  const precinctResults = data?.precinctAssessmentResult ?? [];
  const devResults = data?.devAssessmentResult ?? [];

  const allResults = [...precinctResults, ...devResults];
  const compliantCount = allResults.filter((r) => r.status === 'COMPLIANT').length;
  const overallScore = allResults.length
    ? Math.round((compliantCount / allResults.length) * 100)
    : 0;

  const withEvidence = assessmentData.filter((p) => p.evidence);

  return (
    <>
      <HeaderSection>
        <div>
          <SectionTitle>Assessment Report</SectionTitle>
          <SectionDescription>
            Performance outcome compliance combining extraction and analysis results
          </SectionDescription>
        </div>
        <ActionButtons>
          <DashboardButton type="button" onClick={onDashboard}>
            Dashboard
          </DashboardButton>
          <ContinueButton type="button" onClick={onContinue}>
            Reports <ArrowRight size={14} />
          </ContinueButton>
        </ActionButtons>
      </HeaderSection>

      {/* ── First call in flight, nothing to show yet ────────────── */}
      {assessMutation.isPending && !assessmentQuery.data && (
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Running assessment…</span>
        </LoadingState>
      )}

      {/* ── assessmentsData() failed outright ─────────────────────── */}
      {assessMutation.isError && !assessmentQuery.data && (
        <EmptyState>
          <FileWarning size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Assessment failed</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            {assessMutation.error?.message || 'Something went wrong. Please try again.'}
          </p>
          <CheckAgainButton type="button" onClick={handleCheckAgain}>
            <ClipboardList size={14} /> Try Again
          </CheckAgainButton>
        </EmptyState>
      )}

      {/* ── Marked complete, fetching the full payload ────────────── */}
      {isComplete && assessmentQuery.isLoading && (
        <LoadingState>
          <Loader2 size={22} className="spin" />
          <span>Fetching assessment data…</span>
        </LoadingState>
      )}

      {/* ── Not complete yet — "Check Again" re-triggers ──────────── */}
      {!assessMutation.isPending && assessMutation.isSuccess && !isComplete && (
        <EmptyState>
          <ClipboardList size={36} strokeWidth={1.2} />
          <p style={{ margin: 0, fontWeight: 500 }}>Assessment In Progress</p>
          <p style={{ margin: 0, fontSize: 13 }}>
            Your assessment is still being generated. Check back shortly or try again.
          </p>
          <CheckAgainButton
            type="button"
            onClick={handleCheckAgain}
            disabled={assessMutation.isPending}
          >
            {assessMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Checking…
              </>
            ) : (
              <>
                <ClipboardList size={14} /> Check Again
              </>
            )}
          </CheckAgainButton>
        </EmptyState>
      )}

      {/* ── Query-level error ──────────────────────────────────────── */}
      {isComplete && assessmentQuery.isError && (
        <GlobalError>
          <AlertTriangle size={16} />
          {assessmentQuery.error?.message || 'Failed to load assessment data.'}
        </GlobalError>
      )}

      {/* ── Data arrived ───────────────────────────────────────────── */}
      {isComplete && data && (
        <>
          <CoverPanel>
            <CoverMetaGrid>
              <CoverMetaItem>
                <CoverMetaLabel>Performance Outcomes</CoverMetaLabel>
                <CoverMetaValue>{assessmentData.length}</CoverMetaValue>
              </CoverMetaItem>
              <CoverMetaItem>
                <CoverMetaLabel>Evaluated</CoverMetaLabel>
                <CoverMetaValue>{allResults.length}</CoverMetaValue>
              </CoverMetaItem>
              <CoverMetaItem>
                <CoverMetaLabel>Compliant</CoverMetaLabel>
                <CoverMetaValue>{compliantCount}</CoverMetaValue>
              </CoverMetaItem>
            </CoverMetaGrid>
            <ScoreRing $score={overallScore}>
              <ScoreNum>{overallScore}%</ScoreNum>
              <ScoreLabel>Compliance</ScoreLabel>
            </ScoreRing>
          </CoverPanel>

          {assessmentData.length > 0 && (
            <Section>
              <SectionLabel>
                Performance Outcome Data
                <CountBadge>{withEvidence.length} With Evidence</CountBadge>
              </SectionLabel>
              <PoList>
                {assessmentData.map((po, idx) => (
                  <PoItem key={`${po.poCode}-${idx}`}>
                    <PoCode>{po.poCode}</PoCode>
                    {po.poDescription && <PoDescription>{po.poDescription}</PoDescription>}
                    {po.evidence && (
                      <PoFieldGrid>
                        <PoFieldLabel>Evidence</PoFieldLabel>
                        <PoFieldValue>{po.evidence}</PoFieldValue>
                      </PoFieldGrid>
                    )}
                  </PoItem>
                ))}
              </PoList>
            </Section>
          )}

          <FindingsSection title="Precinct Assessment Result" results={precinctResults} />
          <FindingsSection title="Development Assessment Result" results={devResults} />

          {allResults.length === 0 && assessmentData.length === 0 && (
            <EmptyState>
              <ClipboardList size={36} strokeWidth={1.2} />
              <p style={{ margin: 0 }}>No assessment data available yet.</p>
            </EmptyState>
          )}
        </>
      )}
    </>
  );
};

export default Assessment;
